import type { Form } from "~/models/Form";
import type { FormField } from "~/models/FormField";
import type { FieldDefinition } from "~/models/Automation";
import * as yup from "yup";
import type { WatchStopHandle } from "vue";
import type Color from "~/utils/color";
import { useFormLogic } from "~/stores/forms/logic";
import type { StringSchema } from "yup";
import isArray from "lodash/isArray";
import isEmpty from "lodash/isEmpty";

export type FormStateMode = "response" | "preview" | "builder" | "readonly";
export type FormResponseData = Record<FormField["id"], any>;
export type ActiveFormField = FormField["id"] | null;

export interface FormStateOptions {
    form: MaybeRefOrGetter<Form> | Form;
    mode?: MaybeRefOrGetter<FormStateMode> | FormStateMode;
    activeFieldId?: MaybeRefOrGetter<ActiveFormField> | ActiveFormField;
    variables?: MaybeRefOrGetter<FieldDefinition[]> | FieldDefinition[];
    data?: MaybeRefOrGetter<FormResponseData> | FormResponseData;
}

export interface FormValidationMessages {
    invalid_email: string;
    invalid_url: string;
    min_string: string;
    max_string: string;
    min_number: string;
    max_number: string;
    invalid_pattern: string;
    required: string;
    invalid_number: string;
    invalid_boolean: string;
    invalid_array: string;
    min_range: string;
    max_range: string;
    nullable: string;
}

export const defaultValidationMessages: FormValidationMessages = {
    invalid_email: "Please enter a valid email address.",
    invalid_url: "Please enter a valid website URL.",
    min_string:
        "The entered text needs to be at least {{min}} characters long.",
    max_string: "The entered text cannot be longer than {{max}} characters.",
    min_number: "The entered number needs to be at least {{min}}.",
    max_number: "The entered number cannot be greater than {{max}}.",
    invalid_pattern: "The entered value doesn't match the expected format.",
    required: "This field is required and cannot be empty.",
    nullable:
        "This field can be left empty. Please clear it completely or fill it with a valid value.",
    invalid_number: "Please enter a number (like 42 or 3.14).",
    invalid_boolean: "Please select either Yes or No.",
    invalid_array: "Please select one or more options from the list.",
    min_range: "Invalid range, it must be exactly 2 items.",
    max_range: "Invalid range, it must be exactly 2 items.",
};

export interface FileText {
    upload_title: string;
    upload_description: string;
}

export const defaultFileText: FileText = {
    upload_title: "Drop Files Here",
    upload_description: "Click here or drop files to upload them",
};

export interface PublicForm extends Form {
    field_map: Record<string, any>;
    extra_translations: Record<string, string | null> &
        FormValidationMessages &
        FileText;
}

export const useFormStateStore = defineStore("form-state", () => {
    // State
    const form = ref<PublicForm>();
    const mode = ref<FormStateMode>("readonly");
    const activeFieldId = ref<ActiveFormField | null>(null);
    const data = ref<FormResponseData>({});
    const variables = ref<FieldDefinition[]>([]);
    const trail = ref<FormField["id"][]>([]);

    // Computes
    const isInitialized = computed(
        () => form.value && form.value.fields && form.value.fields.length,
    );

    const collection = computed(() => createFieldCollection(variables.value));

    function replaceFieldContent(field?: FormField) {
        if(!field) return field;
        return field_map.value.replaceContent(
            field_map.value.replaceContent(
                field_map.value.replaceContent(
                    useCloneDeep(field),
                ),
                {
                    prefix: "@",
                    suffix: "@",
                },
            ),
        );
    }

    const activeField = computed(() => {
        return replaceFieldContent((form.value?.fields ?? []).find(
            (field) => field.id === activeFieldId.value,
        ));
    });

    const schema = computed(() => {
        const validationMessages: FormValidationMessages = {
            ...defaultValidationMessages,
        };

        for (const key of Object.keys(
            validationMessages,
        ) as (keyof FormValidationMessages)[]) {
            if (form.value?.extra_translations[`validation.${key}`]) {
                validationMessages[key] = form.value?.extra_translations[
                    `validation.${key}`
                ] as string;
            }
        }

        const spec: Record<string, any> = {};
        let type = "";
        const fld = activeField.value;
        let rule = null;

        if(!fld) {
            return yup.object(spec);
        }

        if (fld.type === "ending") {
            return yup.object(spec);
        }

        if (
            [
                "text",
                "email",
                "phone",
                "url",
                "datetime",
                "date",
                "time",
            ].includes(fld.type)
        ) {
            rule = yup.string();
            type = "string";

            if (fld.type === "email") {
                rule = rule.email(validationMessages.invalid_email);
            }

            if (fld.type === "url") {
                rule = rule.url(validationMessages.invalid_url);
            }

            if (fld.metadata?.validation?.pattern) {
                rule = (rule as StringSchema).matches(
                    fld.metadata.validation.pattern,
                    validationMessages.invalid_pattern,
                );
            }
        } else if (
            ["rating", "currency", "numeric", "number"].includes(fld.type)
        ) {
            type = "number";
            rule = yup.number().typeError(validationMessages.invalid_number);
        } else if (fld.type === "binary") {
            rule = yup.boolean().typeError(validationMessages.invalid_boolean);
        } else if (["choice", "range"].includes(fld.type)) {
            rule = yup.array().typeError(validationMessages.invalid_array);

            const range = createSimpleFieldCollection([
                { default_value: 2, key: "min", type: "number" },
                { default_value: 2, key: "max", type: "number" },
            ]);

            if (fld.type === "range") {
                rule = rule
                    .min(2, range.replaceContent(validationMessages.min_range))
                    .max(2, range.replaceContent(validationMessages.max_range))
                    .of(
                        fld.required
                            ? yup
                                  .number()
                                  .typeError(validationMessages.invalid_number)
                                  .required(validationMessages.required)
                            : yup
                                  .number()
                                  .typeError(validationMessages.invalid_number)
                                  .nullable(validationMessages.nullable),
                    );
            } else if (fld.required) {
                rule = rule.min(1, validationMessages.required);
            }
        } else if (fld.type === "file") {
            rule = yup.mixed().test('is-file-or-string', validationMessages.required, (value) => {
                if (!fld.required && (isEmpty(value))) return true;
                if (fld.required && isEmpty(value)) return false;
                // Valid if it's a string (existing file reference) or a File object
                return typeof value === 'string'
                    || value instanceof File
                    || (isArray(value) && value.every((item) => typeof item === 'string' || item?.asset instanceof File));
            });
        }

        if (!rule) {
            rule = yup.mixed();
        }

        if (rule && fld.required) {
            rule = rule.required(validationMessages.required);
        } else if (rule) {
            rule = rule.nullable(validationMessages.nullable);
        }

        if (rule && fld.metadata?.validation?.min) {
            const fields = createSimpleFieldCollection([
                {
                    default_value: fld.metadata.validation.min,
                    key: "min",
                    type: "number",
                },
            ]);

            rule = (rule as StringSchema).min(
                fld.metadata.validation.min,
                fields.replaceContent(
                    type === "string"
                        ? validationMessages.min_string
                        : validationMessages.min_number,
                ),
            );
        }

        if (rule && fld.metadata?.validation?.max) {
            const fields = createSimpleFieldCollection([
                {
                    default_value: fld.metadata.validation.max,
                    key: "min",
                    type: "number",
                },
            ]);

            rule = (rule as StringSchema).max(
                fld.metadata.validation.max,
                fields.replaceContent(
                    type === "string"
                        ? validationMessages.max_string
                        : validationMessages.max_number,
                ),
            );
        }

        spec[fld.id as string] = rule;
        return yup.object(spec);
    });

    const activeFieldData = computed({
        get: () =>
            activeFieldId.value && data.value
                ? (data.value[activeFieldId.value] ?? null)
                : null,
        set: (value: any) => {
            if (!activeFieldId.value) {
                return;
            }

            data.value = {
                ...data.value,
                [activeFieldId.value]: value,
            };
        },
    });

    const theme = computed(() => {
        const topLeft =
            form.value?.metadata?.theme?.borderTopLeftRadius ?? "8px";
        const bottomLeft =
            form.value?.metadata?.theme?.borderBottomLeftRadius ?? "8px";
        const topRight =
            form.value?.metadata?.theme?.borderTopRightRadius ?? "8px";
        const bottomRight =
            form.value?.metadata?.theme?.borderBottomRightRadius ?? "8px";

        return collection.value.replaceContent({
            logo: form.value?.metadata?.theme.logo ?? "{{brand.email_logo}}",
            font: form.value?.metadata?.theme.font,
            buttonText: form.value?.metadata?.theme.buttonText,
            textColor:
                form.value?.metadata?.theme.textColor ??
                "{{brand.theme.palette.background.text_color}}",
            inputColor:
                form.value?.metadata?.theme.inputColor ??
                "{{brand.theme.palette.surface.text_color}}",
            inputBackgroundColor:
                form.value?.metadata?.theme.inputBackgroundColor ??
                "{{brand.theme.palette.surface.color}}",
            inputVariant: form.value?.metadata?.theme.inputVariant ?? "solid",
            buttonColor:
                form.value?.metadata?.theme.buttonColor ??
                "{{brand.theme.palette.primary.color}}",
            buttonTextColor:
                form.value?.metadata?.theme.buttonTextColor ??
                "{{brand.theme.palette.primary.text_color}}",
            backgroundColor:
                form.value?.metadata?.theme.backgroundColor ??
                "{{brand.theme.palette.background.color}}",
            backgroundImage: form.value?.metadata?.theme.backgroundImage,
            backgroundOpacity: `${form.value?.metadata?.theme.backgroundOpacity ?? 100}%`,
            borderRadius: `${topLeft} ${topRight} ${bottomRight} ${bottomLeft}`,
            alignment: form.value?.metadata?.theme.alignment ?? "left",
            scale: form.value?.metadata?.theme.scale ?? "md",
        });
    });

    const styles = computed(() => {
        return {
            "--form-logo": theme.value.logo,
            "--form-text-color": theme.value.textColor,
            "--form-text-color-light": useColor(theme.value.textColor)
                .adjustRgb(100)
                .toHex(),
            "--form-input-color": theme.value.inputColor,
            "--form-error-input-color": "#ef4444",
            "--form-error-button-color": "#ef4444",
            "--form-border-radius": theme.value.borderRadius,
            "--form-input-placeholder-color": useColor(theme.value.inputColor)
                .setOpacity(50)
                .toRgb(true),
            "--form-input-background-color": theme.value.inputBackgroundColor,
            "--form-input-background-color-soft": useColor(
                theme.value.inputBackgroundColor,
            ).when(
                (color: Color) => color.isDark(-20),
                (color: Color) =>
                    (color.isBlack()
                        ? color
                        : color.clone().adjustRgb(40)
                    ).toRgb(true),
                (color: Color) =>
                    (color.isWhite()
                        ? color
                        : color.clone().adjustRgb(-40)
                    ).toRgb(true),
            ),
            "--form-button-color": theme.value.buttonColor,
            "--form-input-background-color-hover": useColor(
                theme.value.inputBackgroundColor,
            ).when(
                (color: Color) => color.isDark(-20),
                (color: Color) => color.clone().adjustRgb(20).toRgb(true),
                (color: Color) => color.clone().adjustRgb(-20).toRgb(true),
            ),
            "--form-input-background-color-active": useColor(
                theme.value.inputBackgroundColor,
            ).when(
                (color: Color) => color.isDark(-20),
                (color: Color) => color.clone().adjustRgb(40).toRgb(true),
                (color: Color) => color.clone().adjustRgb(-40).toRgb(true),
            ),
            "--form-button-color-hover": useColor(theme.value.buttonColor).when(
                (color: Color) => color.isDark(-20),
                (color: Color) => color.clone().adjustRgb(20).toRgb(true),
                (color: Color) => color.clone().adjustRgb(-20).toRgb(true),
            ),
            "--form-button-color-active": useColor(
                theme.value.buttonColor,
            ).when(
                (color: Color) => color.isDark(-20),
                (color: Color) => color.clone().adjustRgb(40).toRgb(true),
                (color: Color) => color.clone().adjustRgb(-40).toRgb(true),
            ),
            "--form-button-text-color": theme.value.buttonTextColor,
            "--form-background-color": theme.value.backgroundColor,
            "--form-corner-radius": theme.value.cornerRadius,
            "--form-alignment": theme.value.alignment,
        };
    });

    // Functions
    function maybeWatch<T>(
        value: T | MaybeRefOrGetter<T> | null | undefined,
        defaultValue: T | null,
        callback: (value: T) => void,
    ): WatchStopHandle | undefined {
        if (isRef(value)) {
            callback((value.value as T) ?? (defaultValue as T));
            return watch(value, (to) =>
                callback((to as T) ?? (defaultValue as T)),
            );
        } else {
            callback((value as T) ?? (defaultValue as T));
        }
    }

    function initialize(options: FormStateOptions) {
        const stopHandles = [
            maybeWatch<Form>(options.form, null, (value) => {
                form.value = value;
            }),
            maybeWatch<FormStateMode>(options.mode, "readonly", (value) => {
                mode.value = value;
            }),
            maybeWatch<FormResponseData>(options.data, {}, (value) => {
                data.value = value;
            }),
            maybeWatch<ActiveFormField>(
                options.activeFieldId,
                null,
                (value) => {
                    activeFieldId.value = value;
                },
            ),
            maybeWatch<FieldDefinition[]>(options.variables, [], (value) => {
                variables.value = value;
            }),
        ];

        if (!activeFieldId.value && form.value?.fields?.length) {
            activeFieldId.value = form.value.fields[0].id;

            const current = form.value.fields.find(
                (field) => field.id === activeFieldId.value,
            );

            if (current && current.hidden) {
                activeFieldId.value =
                    form.value.fields.find(
                        (field) =>
                            field.position > current.position && !field.hidden,
                    )?.id ?? null;
            }
        }

        return function stopWatchingForm() {
            stopHandles.forEach((stopHandle) => {
                if (stopHandle) stopHandle();
            });
        };
    }

    function handleNext(nextField: FormField) {
        trail.value = [...trail.value, activeFieldId.value];

        if (nextField.type === "ending" && nextField.sub_type === "redirect") {
            data.value[nextField.id] = true;
            store("partial", [activeFieldId.value, nextField.id], nextField.id, nextField);
        } else {
            if (
                (nextField.type !== "ending" ||
                    nextField?.metadata?.timer_enabled) &&
                nextField.id in data.value
            ) {
                store("full", [], nextField.id);
            }
            activeFieldId.value = nextField.id;
        }
    }

    async function next() {
        const logic = useFormLogic();

        if (logic.test()) {
            return;
        }

        const nextField = form.value?.fields?.find(
            (field) =>
                field.position === (activeField.value?.position ?? -1) + 1,
        );

        if (nextField) {
            if (nextField.hidden) {
                next();
            } else {
                handleNext(nextField);
            }
        } else if (["builder", "preview", "readonly"].includes(mode.value)) {
            activeFieldId.value = form.value?.fields[0]?.id ?? null;
            trail.value = [];
        }
    }

    function previous() {
        if (trail.value?.length === 0) return;

        let trl = [...trail.value];
        const previous = trl[trl.length - 1];

        if (previous) {
            activeFieldId.value = previous;
            trl.pop();
            trail.value = trl;
        }
    }

    function canGoNextOnInput(field: FormField) {
        if (!field) return false;
        else
            return (
                ["rating", "binary", "ending"].includes(field.type) ||
                (field.type === "choice" && !field.metadata?.multiple)
            );
    }

    function fixUrl(url: string, defaultScheme = "https"): string {
        // Regex to match a scheme, like "http://", "https://", "ftp://", etc.
        const schemeRegex = /^[a-zA-Z][a-zA-Z\d+\-.]*:/;

        // If the URL already has a scheme, return it as is.
        if (schemeRegex.test(url)) {
            return url;
        }

        // If URL doesn't have a scheme, prepend the defaultScheme
        try {
            // This method will throw an error if the URL is not valid
            let parsedUrl = new URL(`${defaultScheme}://${url}`);
            return parsedUrl.href;
        } catch {
            // Handle specific cases that might not conform to normal URL structure
            return `${defaultScheme}://${url}`;
        }
    }

    function maybeRedirectField(field: FormField) {
        field = replaceFieldContent(field);
        if (field?.type === "ending" && field.metadata?.redirect_url) {
            let url = field?.metadata?.redirect_url as string;
            console.log(url);
            if (!url) return;

            url = fixUrl(url);

            const hasTimer =
                field.sub_type !== "redirect" &&
                field.metadata?.timer_enabled &&
                Number.isInteger(field.metadata?.timer) &&
                field.metadata?.timer;

            const isInIframe = window.top !== window.self;

            if (hasTimer) {
                setTimeout(() => {
                    navigateTo(url, {
                        external: true,
                        open: {
                            target: isInIframe ? "_blank" : "_self",
                        },
                    });
                }, field.metadata?.timer * 1000);
            } else {
                navigateTo(url, {
                    external: true,
                    open: {
                        target: isInIframe ? "_blank" : "_self",
                    },
                });
            }
        }
    }

    async function store(
        type: "full" | "active" | "partial" | "builder" = "active",
        ids: string[] = [],
        nextFieldId?: string,
        redirectField?: FormField,
    ) {
        // When there's no active field, or we're in readonly mode we can't store
        if (!activeFieldId.value || mode.value === "readonly") return;

        // The builder cannot store either and goes to the next question
        if (mode.value === "builder") {
            if (nextFieldId) activeFieldId.value = nextFieldId;
            else if (canGoNextOnInput(activeField.value)) {
                next();
            }

            return;
        }

        let body: Record<string, any> = {
            active_field_id: activeFieldId.value,
        };

        // When the ending activated we will mark it as true
        // so we can identify which respondent was redirected
        // and which ones weren't.
        if (activeField.value?.type === "ending") {
            data.value[activeFieldId.value] = true;
        }

        if (type === "full") {
            body.data = data.value;
        } else if (type === "partial") {
            const bodyData: Record<string, any> = {};

            for (const id of ids ?? []) {
                bodyData[id] = data.value[id];
            }

            body.data = bodyData;
        } else {
            body.data = {
                [activeFieldId.value]: data.value[activeFieldId.value],
            };
        }

        const route = useRoute();

        const cookie = useState("HD-Anonymous-Form-Id");

        return await useApi()
            .request(`public/forms${route.fullPath}`, {
                method: "POST",
                body,
                headers: cookie.value
                    ? {
                          "HD-Anonymous-Form-Id": cookie.value,
                      }
                    : undefined,
            })
            .then((response) => {
                maybeRedirectField(redirectField ?? activeField.value);

                return response;
            });
    }

    const field_map = computed(() => {
        let map_data: Record<string, any> = {};
        let data_value = { ...data.value };

        for (const field of form.value?.fields ?? []) {
            if (
                mode.value === "builder" &&
                (!(field.id in data_value) ||
                    (Array.isArray(data_value[field.id]) &&
                        data_value[field.id].length === 0))
            ) {
                switch (field.type) {
                    case "choice":
                        if (!field.options?.length) continue;

                        data_value[field.id] =
                            field.metadata.multiple && field.options.length > 1
                                ? [
                                      field.options[0].value,
                                      field.options[1].value,
                                  ]
                                : [field.options[0]?.value];
                        break;
                }
            }

            if (!(field.id in data_value)) {
                continue;
            }

            switch (field.type) {
                case "choice":
                    const mapChoice = (num: number, option: any) => {
                        option = field.options?.find(
                            (opt) => opt.value === option,
                        );

                        const key =
                            num === -1
                                ? `${field.id}.answer`
                                : `${field.id}.answer.${num}.`;

                        map_data[`${key}.description`] =
                            option?.description ?? "";
                        map_data[`${key}.avatar`] = option?.avatar ?? "";

                        if (option?.data && typeof option.data === "object") {
                            for (const attribute of Object.keys(option.data)) {
                                map_data[`${key}.${attribute}`] =
                                    option.data[attribute] ?? "";
                            }
                        }

                        map_data[`${key}.id`] = option?.id ?? "";
                        map_data[`${key}.label`] = option?.label ?? "";
                        map_data[`${key}.value`] = option?.value ?? "";
                    };

                    if (field.metadata.multiple) {
                        let num = 0;
                        for (const option of data_value[field.id] ?? []) {
                            mapChoice(num, option);
                            num++;
                        }
                    } else {
                        for (const option of data_value[field.id] ?? []) {
                            mapChoice(-1, option);
                        }
                    }

                    break;
                case "range":
                    map_data[`${field.id}.answer.start`] =
                        data_value[field.id][0];
                    map_data[`${field.id}.answer.end`] =
                        data_value[field.id][1];
                    break;
                default:
                    map_data[`${field.id}.answer`] = data_value[field.id];
                    break;
            }
        }

        return createFieldCollection(form.value?.field_map ?? []).withData(
            map_data,
        );
    });

    return {
        // State
        mode,
        form,
        field_map,
        data,
        activeFieldId,
        variables,
        trail,
        // Computes
        isInitialized,
        collection,
        schema,
        theme,
        styles,
        activeField,
        activeFieldData,
        // Functions
        initialize,
        next,
        previous,
        handleNext,
        store: useDebounce(store, 500),
        logic: () => useFormLogic(),
    };
});

/**
 * This function should only be called once in the application state
 * For deeper calls refer to the store directly
 *
 * @param options
 */
export const useFormState = (options: FormStateOptions) => {
    const store = useFormStateStore();

    return {
        store,
        stopWatchingForm: store.initialize(options),
    };
};
